/* eslint-disable import/order */
import React, { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { Box, Container, Slide, Typography } from '@mui/material';
import { find, get } from 'lodash';

import { ICellRendererParams } from 'ag-grid-enterprise';
import Table from 'src/components/shared/Table';
import { AgGridReact } from 'ag-grid-react';

import { ColDef } from 'ag-grid-community';

import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import RightOnlyBarCell from './RightOnlyBarCell';
import { truncationValues } from 'src/components/Visualization/visualizationConstants';
import {
  formatParameters,
  IFormatOutputParameters,
} from 'src/components/Visualization/formatParameters';

export interface DriverAnalysisChartData {
  ModelID: string;
  Input_Name: string;
  Impact: string;
  Relative_Impact: number;
  Input_Category: string;
  Change: number[];
}

export interface IDriverAnalysisChartStateData {
  ModelID: string;
  Impact: DriverAnalysisChartData['Impact'];
  Input_Name: DriverAnalysisChartData['Input_Name'];
  Relative_Impact: DriverAnalysisChartData['Relative_Impact'];
  Input_Category: DriverAnalysisChartData['Input_Category'];
  Change: DriverAnalysisChartData['Change'];
}

interface GenerateChartPanelProps {
  data: DriverAnalysisChartData[];
  open: boolean;
  formatKey: number | null | undefined;
  // outputName: string;
  // blobURL: string | undefined;
}

const GenerateChartPanel: FunctionComponent<GenerateChartPanelProps> = ({
  data,
  open,
  formatKey,
}) => {
  const [state, setState] = React.useState<{
    reportsData: IDriverAnalysisChartStateData[];
  }>({
    reportsData: [],
  });

  useEffect(() => {
    const reportsData = data.map((d, index) => ({
      ...d,
      ModelID: String(index),
      Change: [d.Relative_Impact],
    }));
    setState({ reportsData });
  }, [data]);

  const gridRef = useRef<AgGridReact<any>>(null);

  const sizeToFit = () => {
    gridRef.current?.api.sizeColumnsToFit();
  };

  const magnitudeTruncation = get(
    find(formatParameters, {
      formatKey,
    }) as IFormatOutputParameters,
    'magnitudeTruncation',
    0
  );

  const decimalPlacesValue = get(
    find(formatParameters, {
      formatKey,
    }) as IFormatOutputParameters,
    'decimalPlaces',
    0
  );

  const formattedImpact = useMemo(() => {
    return (value: number) => {
      const result = (value / truncationValues[String(magnitudeTruncation)]).toFixed(
        decimalPlacesValue
      );
      // Handle negative zero case - show as "0" instead of "-0"
      return result === '-0' ? '0' : result;
    };
  }, [truncationValues, magnitudeTruncation, decimalPlacesValue]);

  const ModelRenderer = (
    cellRendererParams: ICellRendererParams<IDriverAnalysisChartStateData>
  ) => {
    const modelId = cellRendererParams.data?.ModelID;
    const data = state.reportsData;
    const model = find(data, { ModelID: modelId });
    const field = cellRendererParams?.colDef
      ?.field as keyof IDriverAnalysisChartStateData;
    return model ? (
      <Typography
        component="span"
        variant="body2"
        sx={{
          textWrap: 'auto',
          WebkitLineClamp: 3,
          textOverflow: 'ellipsis',
          maxHeight: 'calc(1.57em*3)',
          overflow: 'hidden',
          display: 'inline-flex',
          alignItems: 'center',
          height: '100%',
        }}
      >
        {field === 'Impact'
          ? formattedImpact(Number(model[field]))
          : model[field]}
        {field === 'Relative_Impact' ? '%' : ''}
      </Typography>
    ) : (
      ''
    );
  };

  // compute a global maxAbs once per render (so bars scale consistently)
  const maxAbsChange = Math.max(
    100,
    ...state.reportsData.map((r) => {
      const raw = Array.isArray(r.Change) ? r.Change[0] : r.Change;
      const n = Number(raw);
      return Number.isFinite(n) ? Math.abs(n) : 0;
    })
  );

  const reportColumns: ColDef<IDriverAnalysisChartStateData>[] = [
    {
      field: 'Input_Category',
      headerName: 'Input Category',
      headerTooltip: 'Input Category',
      sortable: true,
      cellRenderer: ModelRenderer,
      flex: 15,
      minWidth: 100,
    },
    {
      field: 'Input_Name',
      headerName: 'Input Name',
      headerTooltip: 'Input Name',
      sortable: true,
      cellRenderer: ModelRenderer,
      flex: 20,
      minWidth: 100,
    },
    {
      field: 'Impact',
      headerName: 'Impact',
      headerTooltip: 'Impact',
      cellRenderer: ModelRenderer,
      sortable: true,
      flex: 15,
      minWidth: 100,
    },
    {
      field: 'Relative_Impact',
      headerName: 'Relative Impact (%)',
      headerTooltip: 'Relative Impact (%)',
      cellRenderer: ModelRenderer,
      flex: 15,
      minWidth: 100,
    },
    {
      field: 'Change',
      headerTooltip: 'Change',
      headerName: 'Change',
      valueGetter: (p: any) => {
        const raw = Array.isArray(p.data.Change)
          ? p.data.Change[0]
          : p.data.Change;
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
      },
      cellRenderer: RightOnlyBarCell,
      cellRendererParams: {
        maxAbs: maxAbsChange, // scale all rows by the same max |value|
        negColor: '#C23531',
        posColor: '#1271C4',
      },
      sortable: true,
      flex: 35,
      minWidth: 300,
    },
  ];
  return (
    <Box>
      <Slide direction="right" in={open} mountOnEnter unmountOnExit>
        <Container
          maxWidth={false}
          sx={{
            mb: 2,
            mt: 2,
            pt: 0,
          }}
        >
          <Box
            sx={{
              justifyContent: 'end',
              display: 'flex',
              alignItems: 'center',
              paddingBottom: '12px',
            }}
          >
            {/* TODO: Download Logic can be implemented here */}
            {/* <Tooltip title="Download Waterfall Report" arrow placement="left">
              <Link href={blobURL} download>
                <IconButton>
                  <DownloadIcon color="primary" />
                </IconButton>
              </Link>
            </Tooltip> */}
          </Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Driver Analysis - Relative Impact Visualization
          </Typography>
          <Table
            defaultColDef={{
              autoHeight: true,
              suppressMenu: true,
            }}
            gridRef={gridRef}
            id="reports-table"
            isLoading={false}
            isSuccess
            pagination={false}
            rowCount={state.reportsData.length}
            rowData={state.reportsData}
            columnDefs={reportColumns}
            suppressRowClickSelection
            suppressPaginationPanel
            onGridReady={sizeToFit}
            masterDetail
            gridOptions={{
              enableBrowserTooltips: true,
            }}
          />
        </Container>
      </Slide>
    </Box>
  );
};

export default GenerateChartPanel;
